import {
  ColorOperator,
  MatchingEntity,
  MatchingNotesSection,
  MatchingOperator,
  MatchingRuleKind,
  RuleDryRunSkipReason,
  RuleFamily,
  RuleLogic,
  RuleOperator,
  RuleType,
} from '../types';

// URL segments are kebab-case, the GraphQL enum values camelCase — only `colorCoding` differs.
export const FAMILY_SLUGS: Record<RuleFamily, string> = {
  [RuleFamily.watchlist]: 'watchlist',
  [RuleFamily.colorCoding]: 'color-coding',
  [RuleFamily.matching]: 'matching',
};

export const SLUG_FAMILIES: Record<string, RuleFamily> = {
  watchlist: RuleFamily.watchlist,
  'color-coding': RuleFamily.colorCoding,
  matching: RuleFamily.matching,
};

// All three families now have a dry-run engine (SAA-105 shipped colour, 03-matching PLAN ships
// matching).
export const DRY_RUN_SUPPORTED_FAMILIES: RuleFamily[] = [
  RuleFamily.watchlist,
  RuleFamily.colorCoding,
  RuleFamily.matching,
];

// The editor's URL segment for a rule that does not exist yet — one form, empty vs populated.
export const NEW_RULE_SEGMENT = 'new';

export const SEVERITY_VALUES = ['low', 'medium', 'high', 'critical'] as const;

export const SEVERITY_LABEL_COLOR: Record<string, string> = {
  critical: 'error',
  high: 'warning',
  medium: 'info',
  low: 'grey',
};

// The skip reasons that mean "could not be evaluated". `notApplicable` is deliberately absent: it
// is the ordinary no-fire and applies to most of the catalogue on any run. Serves BOTH families —
// `ruleDisabled`/`ruleTypeUnsupported`/`gateFieldAbsent`/`modelUnavailable` are the colour family's
// own evaluation-gap reasons (`noCaseMatched`/`gateNotMatched` are colour's ordinary no-match and
// are deliberately absent here too, same reasoning as `notApplicable`).
export const UNEVALUABLE_SKIP_REASONS: RuleDryRunSkipReason[] = [
  RuleDryRunSkipReason.disabled,
  RuleDryRunSkipReason.fieldAbsent,
  RuleDryRunSkipReason.unresolved,
  RuleDryRunSkipReason.inferenceDeferred,
  RuleDryRunSkipReason.ruleDisabled,
  RuleDryRunSkipReason.ruleTypeUnsupported,
  RuleDryRunSkipReason.gateFieldAbsent,
  RuleDryRunSkipReason.modelUnavailable,
];

export const RULE_TYPE_VALUES = [RuleType.exact, RuleType.ai] as const;

export const LOGIC_VALUES = [RuleLogic.all, RuleLogic.any] as const;

// Operators that take NO value at all — mirrors `VALUELESS_OPERATORS` (backend `field-catalogue.ts`).
export const VALUELESS_OPERATORS: RuleOperator[] = [
  RuleOperator.isBlank,
  RuleOperator.isNotBlank,
  RuleOperator.isEmpty,
  RuleOperator.blankOrRepeatedCharacters,
];

// Operators whose value is a NUMBER whatever the field's own type is (a length, a window, a count).
export const NUMERIC_OPERATORS: RuleOperator[] = [
  RuleOperator.textLengthLt,
  RuleOperator.textLengthGt,
  RuleOperator.withinLastMonths,
  RuleOperator.olderThanMonths,
  RuleOperator.olderThanYears,
  RuleOperator.countGte,
  RuleOperator.countLte,
  RuleOperator.lt,
  RuleOperator.lte,
  RuleOperator.gt,
  RuleOperator.gte,
];

// Operators taking a LIST of option keys.
export const LIST_OPERATORS: RuleOperator[] = [RuleOperator.in, RuleOperator.includesAny];

export const NUMERIC_VALUE_PATTERN = /^-?\d+(\.\d+)?$/;

export const BOOLEAN_VALUES = ['true', 'false'] as const;

// ── Colour coding (color-coding-rules-ui) ───────────────────────────────────────────────────────
export const COLOR_RULE_TYPE_VALUES = [RuleType.exact, RuleType.ai] as const;

export const COLOR_GATE_KIND_VALUES = ['override', 'cap'] as const;

export const COLOR_CODE_COLOR_VALUES = ['unicorn', 'green', 'yellow', 'orange', 'red'] as const;

// Every colour operator (name spelling) that takes NO value at all.
export const COLOR_VALUELESS_OPERATORS: ColorOperator[] = [ColorOperator.isTrue, ColorOperator.isFalse];

// Every colour operator whose value is a LIST of option keys / scalars.
export const COLOR_LIST_VALUE_OPERATORS: ColorOperator[] = [ColorOperator.in, ColorOperator.notIn];

// ── Matching (03-matching) ──────────────────────────────────────────────────────────────────────
export const MATCHING_RULE_KIND_VALUES = [
  MatchingRuleKind.exclusion,
  MatchingRuleKind.blocking,
  MatchingRuleKind.compatibility,
] as const;

export const MATCHING_ENTITY_VALUES = [MatchingEntity.gc, MatchingEntity.ip] as const;

export const MATCHING_NOTES_SECTION_VALUES: string[] = Object.values(MatchingNotesSection);

// The stored value stays the engine's internal section name; the screen shows the heading staff type (SAA-288).
export const MATCHING_NOTES_SECTION_LABEL_KEYS: Record<MatchingNotesSection, string> = {
  [MatchingNotesSection.gcProfilesDenied]: 'rules.matching.source.notesSectionOption.gcProfilesDenied',
  [MatchingNotesSection.ipProfilesDenied]: 'rules.matching.source.notesSectionOption.ipProfileSent',
  [MatchingNotesSection.notesForMatching]: 'rules.matching.source.notesSectionOption.notesForMatching',
  [MatchingNotesSection.blurb]: 'rules.matching.source.notesSectionOption.blurb',
};

export const MATCHING_RULE_TYPE_VALUES = [RuleType.exact, RuleType.ai] as const;

export const MATCHING_GRADE_VALUES = {
  exclusion: ['excluded', 'notExcluded'] as const,
  blocking: ['clear', 'blocking'] as const,
  compatibility: ['match', 'partial', 'mismatch'] as const,
};

// Operators whose condition carries no `value`/`right` at all.
// `is_true`/`is_false` are valueless too (the boolean IS the operator) — a bare
// `{operator: 'equals', value: true}` would express the same test, but the backend rejects a
// `value` alongside these two specifically ("takes no value").
export const MATCHING_VALUELESS_OPERATORS: MatchingOperator[] = [
  MatchingOperator.isBlank,
  MatchingOperator.isNotBlank,
  MatchingOperator.isTrue,
  MatchingOperator.isFalse,
];

// Cross-entity operators: the condition's `right` operand is a field on the OTHER entity.
export const MATCHING_CROSS_ENTITY_OPERATORS: MatchingOperator[] = [
  MatchingOperator.equalsField,
  MatchingOperator.includesAnyField,
  MatchingOperator.dateDiffWithinDays,
  MatchingOperator.dateDiffWithinMonths,
];

// Cross-entity operators that carry NO literal `value` at all (only `right`) — the date-diff pair
// is cross-entity AND carries a numeric window `value`, so it is deliberately excluded here.
export const MATCHING_RIGHT_ONLY_OPERATORS: MatchingOperator[] = [
  MatchingOperator.equalsField,
  MatchingOperator.includesAnyField,
];

// Operators whose value is a NUMBER whatever the field's own type is (a threshold, or a day/month
// window for the two date-diff operators).
export const MATCHING_NUMERIC_OPERATORS: MatchingOperator[] = [
  MatchingOperator.gte,
  MatchingOperator.lte,
  MatchingOperator.dateDiffWithinDays,
  MatchingOperator.dateDiffWithinMonths,
];

export const MATCHING_LIST_VALUE_OPERATORS: MatchingOperator[] = [
  MatchingOperator.in,
  MatchingOperator.notIn,
  MatchingOperator.includesAny,
];
